/**
 * React bindings for the agent-working store: who is working on a card,
 * and the ring class a card root should wear while someone is.
 *
 * The WebSocket subscription lives in `use-agent-working-realtime.ts` so
 * this file stays a leaf — the board / dashboard / card query hooks import
 * the seeding helper from here, and a realtime hook that imported the
 * board's query keys back would close an import cycle.
 */

import { useSyncExternalStore } from 'react'

import { type SeedableCard, type WorkingAgent, agentWorkingStore } from './agent-working-store'

export type { WorkingAgent } from './agent-working-store'

/** Agents currently working on `cardId` (stable array; empty when none). */
export function useWorkingAgents(cardId: string | null | undefined): readonly WorkingAgent[] {
  return useSyncExternalStore(
    agentWorkingStore.subscribe,
    () => agentWorkingStore.getAgents(cardId),
    () => agentWorkingStore.getAgents(cardId),
  )
}

/**
 * Feed a fetched payload's `workingAgents` into the store. Call it from the
 * query function, with the time the request was SENT, so a start frame that
 * arrives while the request is in flight isn't clobbered by the older
 * snapshot.
 */
export function seedWorkingAgents(
  cards: ReadonlyArray<SeedableCard>,
  requestedAt: number = Date.now(),
): void {
  agentWorkingStore.seed(cards, requestedAt)
}

// --- reduced motion --------------------------------------------------------
//
// One shared media-query listener for the whole page. `useReducedMotion()`
// attaches a listener per caller, which is the right cost for one popover and
// the wrong one for a board that mounts hundreds of cards, each of which needs
// the answer only while it happens to be glowing.

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

let reducedMql: MediaQueryList | null = null

function getReducedMql(): MediaQueryList | null {
  if (reducedMql) return reducedMql
  if (typeof window === 'undefined' || !window.matchMedia) return null
  reducedMql = window.matchMedia(REDUCED_MOTION_QUERY)
  return reducedMql
}

function subscribeReducedMotion(onChange: () => void): () => void {
  const mql = getReducedMql()
  if (!mql) return () => {}
  mql.addEventListener('change', onChange)
  return () => mql.removeEventListener('change', onChange)
}

const getReducedMotion = (): boolean => getReducedMql()?.matches ?? false

/** Test seam: forget the cached media query list between cases. */
export function resetSharedReducedMotion(): void {
  reducedMql = null
}

export function useSharedReducedMotion(): boolean {
  return useSyncExternalStore(subscribeReducedMotion, getReducedMotion, () => false)
}

// --- the ring --------------------------------------------------------------

/** Base ring class (`.agent-working` in `global.css`). */
export const AGENT_WORKING_RING_CLASS = 'agent-working'
/** Added when the viewer prefers reduced motion: same ring, no pulse. */
export const AGENT_WORKING_STATIC_CLASS = 'agent-working--static'

export interface AgentWorkingCardState {
  agents: readonly WorkingAgent[]
  /** True while at least one agent is working on the card. */
  working: boolean
  /**
   * Class string for the card root: empty when idle, the ring (plus the
   * static modifier under reduced motion) when an agent is working. Always
   * safe to interpolate.
   */
  ringClass: string
}

export function useAgentWorkingCard(cardId: string | null | undefined): AgentWorkingCardState {
  const agents = useWorkingAgents(cardId)
  const reduced = useSharedReducedMotion()
  const working = agents.length > 0
  return {
    agents,
    working,
    ringClass: working
      ? reduced
        ? `${AGENT_WORKING_RING_CLASS} ${AGENT_WORKING_STATIC_CLASS}`
        : AGENT_WORKING_RING_CLASS
      : '',
  }
}
